import { Grid } from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Button from "../../../framework/Button";
import GlobalContext from "../../../framework/GlobalContext";
import NumberInput from "../../../framework/NumberInput";
import SelectInput from "../../../framework/SelectInput";
import Shortcut from "../../../framework/Shortcut";
import TextInput from "../../../framework/TextInput";
import { displayToast } from "../../../framework/toast/toastUtils";
import {
  getCustomerCategories,
  saveCustomerCategory,
} from "../../../services/customer-category-services";
import classes from "./index.module.css";
import { debounceCategoryList } from "./customerCategoryUtils";

const AddCustomerCategory = ({ onEscape = () => {} }) => {
  const { setCurrentComponent } = useContext(GlobalContext);
  const initial_state = useMemo(() => {
    return {
      customerCategory: null,
      customerCategoryShortName: "",
      discountPercent: null,
    };
  }, []);
  const [errorStates, setErrorStates] = useState({});

  const [formState, setFormState] = useState(initial_state);
  const [customerCategoryOptions, setCustomerCategoryOptions] = useState([]);

  const saveCustomerCategoryRef = useRef();

  const validateForm = useCallback((fieldName, data) => {
    if (fieldName === "name" || !fieldName) {
      if (!data?.name) {
        displayToast({
          type: "error",
          title: "Please enter Entity Category Name",
        });
        setErrorStates((prev) => {
          return { ...prev, email: true };
        });
        return false;
      }
    }

    return true;
  }, []);

  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        return onEscape();
      }
      if (event.ctrlKey && key === "s") {
        return saveCustomerCategoryRef.current.click();
      }
    },
    [onEscape]
  );

  // const getCustomerCategoryOptions = useCallback(async () => {
  //   const response = await getCustomerCategories();
  //   if (response.status === "success") {
  //     setCustomerCategoryOptions(response.result?.rows);
  //   }
  // }, []);

  const resetForm = useCallback(() => {
    setFormState(initial_state);
  }, [initial_state]);

  const saveCustomerCategoryHandler = useCallback(async () => {
    const paylaod = {
      id: formState?.customerCategory?.id || undefined,
      patientCategoryName: formState?.customerCategory?.name,
      categoryShortName: formState?.customerCategoryShortName,
      discountPercent: formState?.discountPercent,
    };
    if (!validateForm(null, { name: paylaod.patientCategoryName })) return;
    const response = await saveCustomerCategory(paylaod);
    if (response?.status === "success") {
      displayToast({
        type: "success",
        title: "Entity category updated successfully",
      });
      onEscape();
    }
  }, [
    formState?.customerCategory?.id,
    formState?.customerCategory?.name,
    formState?.customerCategoryShortName,
    formState?.discountPercent,
    onEscape,
    validateForm,
  ]);

  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);

  useEffect(() => {
    return () => {
      resetForm();
    };
  }, [resetForm]);

  return (
    <div className={classes["add-customer-category-container"]}>
      <Grid container spacing={2} columns={2}>
        <Grid item xs={1}>
          <SelectInput
            id="customer_category_name"
            autoFocus
            freeSolo
            onFocus={() => {
              setCurrentComponent("customer_category_name");
            }}
            onChange={(e, newValue) => {
              setFormState(() => {
                return {
                  customerCategory: newValue,
                  customerCategoryShortName: newValue?.shortName,
                  discountPercent: newValue?.discountPercent,
                };
              });
              if (errorStates?.name)
                setErrorStates((prev) => {
                  return { ...prev, name: false };
                });
            }}
            placeholder="Search/Add Category Here..."
            getOptionLabel={(option) => option.name || option}
            isOptionEqualToValue={(option, value) => {
              return option?.name === value.name || value === "";
            }}
            value={formState?.customerCategory}
            label="Entity Category Name"
            options={customerCategoryOptions}
            onInputChange={(e, value) => {
              if (!e) return;
              setCustomerCategoryOptions([]);
              if (value?.length > 0) {
                // getCustomerCategoryOptions();
                debounceCategoryList(
                  getCustomerCategories,
                  setCustomerCategoryOptions
                );
              }
              if (value?.length === 0) {
                resetForm();
              } else {
                setFormState((prev) => {
                  return {
                    ...prev,
                    customerCategory: { name: e.target.value },
                  };
                });
              }
            }}
            markers={JSON.stringify({
              down: "customer_category_discper",
              left: "customer_category_name",
              right: "customer_category_shortname",
              up: "customer_category_name",
            })}
            error={errorStates?.name}
          />
        </Grid>
        <Grid item xs={1}>
          <TextInput
            id="customer_category_shortname"
            label="Category Short Name"
            onFocus={() => setCurrentComponent("customer_category_shortname")}
            value={formState?.customerCategoryShortName}
            onChange={(e) =>
              setFormState((prev) => {
                return {
                  ...prev,
                  customerCategoryShortName: e.target.value,
                };
              })
            }
            inputProps={{
              "data-markers": JSON.stringify({
                down: "customer_category_discper",
                left: "customer_category_name",
                right: "customer_category_discper",
                up: "customer_category_shortname",
              }),
            }}
          />
        </Grid>
        <Grid item xs={1}>
          <NumberInput
            id="customer_category_discper"
            label="Discount %"
            allowNegative={false}
            onFocus={() => setCurrentComponent("customer_category_discper")}
            value={formState?.discountPercent || 0}
            isAllowed={(data) => {
              return data.floatValue <= 100;
            }}
            allowEmptyFormatting={true}
            onValueChange={(values) => {
              setFormState((prev) => ({
                ...prev,
                discountPercent: values.floatValue,
              }));
            }}
            decimalScale={2}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "customer_category_name",
                down: "customer_category_discper",
                left: "customer_category_shortname",
                right: "customer_category_discper",
              }),
            }}
          />
        </Grid>
      </Grid>
      <div className={classes["add-customer-category-container-footer"]}>
        <Button
          ref={saveCustomerCategoryRef}
          label="Save"
          color="violet"
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          onClick={saveCustomerCategoryHandler}
        />
      </div>
    </div>
  );
};

export default AddCustomerCategory;
